import { type FC, type MouseEvent, useMemo, useState } from "react";
import {
  HeaderCellTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "~/common/components/Table/TableElements";
import TVAccountSelectionResultsTableRow from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/TVAccountSelectionResultsTableRow";
import { SortDirection, useSorting } from "~/common/hooks/useSorting";
import { type AccountInfoData } from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/api/GetAccountList";
import { useTranslation } from "react-i18next";
import classes from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/TVAccountSelectionResultsTable.module.less";
import { NoResultsFound } from "~/pages/AccountSelectionPage/AccountSelectionPage";
import { splitBy } from "~/common/utils/reducers";

interface Props {
  rows: AccountInfoData[];
  selectedId: string;
  onRadioClickChangeActiveState: (item: AccountInfoData) => void;
}

enum SortTypes {
  EndCustomerName = "EndCustomerName",
  AccountName = "AccountName",
  Subregion = "Subregion",
  Sector = "Sector",
  Currency = "Currency",
}

const TVAccountSelectionResultsTable: FC<Props> = (props) => {
  const { t } = useTranslation();
  const [sortCode, setSortCode] = useState<string>(SortTypes.AccountName);
  const [sortDirection, setSortDirection] = useState(SortDirection.Asc); // sadly because of arrow direction desc = asc sorting

  const [selected, notSelected] = useMemo(() => splitBy(props.rows, (row) => row.isDefault), [props.rows]);
  const sortedNotSelected = useSorting(notSelected, SORT_HANDLERS, sortCode, sortDirection);
  const rows = useMemo(() => [...selected, ...sortedNotSelected], [selected, sortedNotSelected]);

  const createSortHandler = (codeToSet: string) => (e: MouseEvent<unknown>) => {
    if (codeToSet === sortCode) {
      setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      setSortCode(codeToSet);
      setSortDirection(SortDirection.Asc);
    }
  };

  const createTableProps = (boundSortCode: string) => ({
    onClick: createSortHandler(boundSortCode),
    active: boundSortCode === sortCode,
    direction: boundSortCode === sortCode ? sortDirection : SortDirection.Desc,
  });

  const headerT = (key: string) => t(`accountSelectionPage.table.header.${key}`);

  if (props.rows.length === 0) {
    return <NoResultsFound />;
  }

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>
            <HeaderCellTitle header={headerT("select")} />
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerCell} {...createTableProps(SortTypes.EndCustomerName)}>
              <HeaderCellTitle
                header={headerT("endCustomerName")}
                subheader={headerT("endCustomerNumber")}
                subHeaderClass="pt-1"
              />
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerCell} {...createTableProps(SortTypes.AccountName)}>
              <HeaderCellTitle
                header={headerT("accountName")}
                subheader={headerT("accountNumber")}
                subHeaderClass="pt-1"
              />
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerCell} {...createTableProps(SortTypes.Subregion)}>
              <HeaderCellTitle header={headerT("subregion")} />
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerCell} {...createTableProps(SortTypes.Sector)}>
              <HeaderCellTitle header={headerT("sector")} />
            </TableSortLabel>
          </TableCell>
          <TableCell>
            <TableSortLabel className={classes.headerCell} {...createTableProps(SortTypes.Currency)}>
              <HeaderCellTitle header={headerT("currency")} />
            </TableSortLabel>
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((rowData) => (
          <TVAccountSelectionResultsTableRow
            key={rowData.id}
            item={rowData}
            isSelected={rowData.id === props.selectedId}
            onRadioClickChangeActiveState={props.onRadioClickChangeActiveState}
          />
        ))}
      </TableBody>
    </Table>
  );
};

export default TVAccountSelectionResultsTable;

type SortFn = SortHandler<AccountInfoData>;

const isDefaultSorting: SortFn = (a: AccountInfoData, b: AccountInfoData) => +a.isDefault - +b.isDefault;
const withDefaultSorting =
  (fn: SortFn): SortFn =>
  (a, b) => {
    let res = isDefaultSorting(a, b);
    if (!res) {
      res = fn(a, b);
    }
    return res;
  };

const SORT_HANDLERS: Dict<SortFn> = {
  [SortTypes.EndCustomerName]: withDefaultSorting((a, b) => a.endCustomerName.localeCompare(b.endCustomerName)),
  [SortTypes.AccountName]: withDefaultSorting((a, b) => a.name.localeCompare(b.name)),
  [SortTypes.Subregion]: withDefaultSorting((a, b) => a.region.localeCompare(b.region)),
  [SortTypes.Sector]: withDefaultSorting((a, b) => a.endCustomerSector.localeCompare(b.endCustomerSector)),
  [SortTypes.Currency]: withDefaultSorting((a, b) => a.currency!.localeCompare(b.currency!)),
};
